"use client";

/**
 * HtmlViewerPanel — responsive wrapper for a sandboxed HTML viewer.
 *
 * Mobile uses a full-screen dialog; desktop uses a resizable right sidebar.
 */

import { AnimatePresence } from "motion/react";
import { FileCode, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { HtmlViewer } from "@/components/html-viewer";
import { HtmlViewerSidebar } from "@/components/html-viewer-sidebar";
import { HtmlViewerToolbar } from "@/components/html-viewer-toolbar";
import { useHtmlFile } from "@/hooks/use-html-file";
import type { AsidePanelResize } from "@/hooks/use-aside-view";
import { useIsMobile } from "@/hooks/use-media-query";
import { isXlsxFile, xlsxPreviewUrl } from "@/lib/file-links";

export interface HtmlViewerPanelProps {
  /** Signed file-download URL (`/files/d/{jwt}`). */
  url: string;
  /** Filename for display (from the JWT fn claim or link label). */
  fileName: string;
  onClose: () => void;
  /** Signed file URL for the "open in new tab" pop-out. */
  fileUrl?: string;
  /** Called when the user wants to open the viewer in a new tab. */
  onOpenInNewWindow?: () => void;
  /** Shared desktop resize controller from the active aside manager. */
  resize: AsidePanelResize;
}

export function HtmlViewerPanel({
  url,
  fileName,
  onClose,
  fileUrl,
  onOpenInNewWindow,
  resize,
}: HtmlViewerPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const workbook = isXlsxFile(url, fileName);
  const { content, loading, error, refetch, refreshed } = useHtmlFile(workbook ? xlsxPreviewUrl(url) : url);
  if (isMobile) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent
          className="flex h-full max-h-full w-full max-w-full flex-col gap-0 overflow-hidden rounded-none p-0"
          hideCloseButton
          data-testid="html-viewer-dialog"
        >
          <DialogTitle className="sr-only">{fileName}</DialogTitle>
          <div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <FileCode className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm font-semibold" title={fileName}>
                {fileName}
              </span>
              {refreshed && (
                <span
                  className="size-1.5 shrink-0 rounded-full bg-blue-500"
                  title={t("viewer.contentUpdated")}
                  data-testid="html-viewer-refreshed-indicator"
                />
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <HtmlViewerToolbar
                content={content}
                fileName={fileName}
                fileUrl={fileUrl}
                sourceUrl={workbook ? url : undefined}
                onOpenInNewWindow={onOpenInNewWindow}
              />
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                onClick={onClose}
                aria-label={t("viewer.closeViewer")}
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
            {loading ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                <Spinner className="size-5" />
                <p className="text-sm">{t("common:status.loadingDocument")}</p>
              </div>
            ) : error ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
                <p className="text-sm text-destructive">{error}</p>
                <Button variant="outline" size="sm" onClick={refetch}>
                  {t("common:actions.retry")}
                </Button>
              </div>
            ) : content != null ? (
              <HtmlViewer content={content} fileName={fileName} />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <AnimatePresence>
      <HtmlViewerSidebar
        content={content}
        loading={loading}
        error={error}
        fileName={fileName}
        onClose={onClose}
        onRetry={refetch}
        refreshed={refreshed}
        width={resize.width}
        fileUrl={fileUrl}
        sourceUrl={workbook ? url : undefined}
        onOpenInNewWindow={onOpenInNewWindow}
        onResizePointerDown={resize.onPointerDown}
        onResizePointerMove={resize.onPointerMove}
        onResizePointerUp={resize.onPointerUp}
        onResizeDoubleClick={resize.onDoubleClick}
      />
    </AnimatePresence>
  );
}
